/**
 * SuppliersSettingsView — ניהול ספקים
 *
 * Phase 12: Full CRUD interface for the  inventory_suppliers  table.
 *
 * Columns managed:
 *   supplier_number  — int, GENERATED BY DEFAULT AS IDENTITY (auto, read-only in form)
 *   name             — text, required
 *   contact_person   — text, optional
 *   tax_id           — text, optional  (ח.פ / עוסק מורשה)
 *   email            — text, optional
 *   phone            — text, optional
 *   payment_terms    — text, optional  (Net 30, שוטף+30…)
 *   currency         — 'ILS' | 'USD' | 'EUR', default ILS
 *   address          — text, optional
 *   notes            — text, optional
 */

import { useEffect, useState, useCallback } from 'react';
import { Truck, Plus, Pencil, Trash2, X, Loader as Loader2, CircleAlert as AlertCircle, Phone, Mail, Hash, Search, FileText, CreditCard, MapPin, User, Clock, DollarSign, BadgeCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InventorySupplier {
  id:              string;
  company_id:      string;
  supplier_number: number;
  name:            string;
  contact_person:  string | null;
  tax_id:          string | null;
  email:           string | null;
  phone:           string | null;
  payment_terms:   string | null;
  currency:        'ILS' | 'USD' | 'EUR';
  address:         string | null;
  notes:           string | null;
  created_at:      string;
  updated_at:      string;
}

type SupplierCurrency = 'ILS' | 'USD' | 'EUR';

interface SupplierForm {
  name:           string;
  contact_person: string;
  tax_id:         string;
  email:          string;
  phone:          string;
  payment_terms:  string;
  currency:       SupplierCurrency;
  address:        string;
  notes:          string;
}

const EMPTY_FORM: SupplierForm = {
  name:           '',
  contact_person: '',
  tax_id:         '',
  email:          '',
  phone:          '',
  payment_terms:  '',
  currency:       'ILS',
  address:        '',
  notes:          '',
};

const CURRENCIES: { value: SupplierCurrency; label: string; symbol: string }[] = [
  { value: 'ILS', label: 'שקל ישראלי',  symbol: '₪' },
  { value: 'USD', label: 'דולר אמריקאי', symbol: '$' },
  { value: 'EUR', label: 'אירו',          symbol: '€' },
];

const PAYMENT_TERM_PRESETS = [
  'שוטף',
  'שוטף+30',
  'שוטף+45',
  'שוטף+60',
  'Net 30',
  'Net 45',
  'Net 60',
  'מיידי',
];

interface SuppliersSettingsViewProps {
  companyId: string | null;
  showToast: (msg: string, ok?: boolean) => void;
}

// ─── Currency symbol helper ───────────────────────────────────────────────────

function currencySymbol(c: SupplierCurrency): string {
  return CURRENCIES.find(x => x.value === c)?.symbol ?? c;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SuppliersSettingsView({ companyId, showToast }: SuppliersSettingsViewProps) {

  // ── State ──────────────────────────────────────────────────────────────────
  const [suppliers,  setSuppliers]  = useState<InventorySupplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState<InventorySupplier | null>(null);
  const [form,       setForm]       = useState<SupplierForm>(EMPTY_FORM);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [saving,     setSaving]     = useState(false);
  const [modalTab,   setModalTab]   = useState<'general' | 'financial'>('general');

  const [delTarget,  setDelTarget]  = useState<InventorySupplier | null>(null);
  const [deleting,   setDeleting]   = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchSuppliers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    const { data, error: err } = await supabase
      .from('inventory_suppliers')
      .select(`
        id, company_id, supplier_number, name, contact_person, tax_id,
        email, phone, payment_terms, currency, address, notes,
        created_at, updated_at
      `)
      .eq('company_id', companyId)
      .order('supplier_number');
    if (err) {
      console.error('[SuppliersSettingsView] SELECT error:', err);
      setError(`שגיאה בטעינת ספקים: ${err.message} (קוד: ${err.code})`);
    } else {
      setSuppliers((data as InventorySupplier[]) ?? []);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalTab('general');
    setModalOpen(true);
  };

  const openEdit = (s: InventorySupplier) => {
    setEditing(s);
    setForm({
      name:           s.name,
      contact_person: s.contact_person ?? '',
      tax_id:         s.tax_id         ?? '',
      email:          s.email          ?? '',
      phone:          s.phone          ?? '',
      payment_terms:  s.payment_terms  ?? '',
      currency:       s.currency,
      address:        s.address        ?? '',
      notes:          s.notes          ?? '',
    });
    setFormError(null);
    setModalTab('general');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const setField = <K extends keyof SupplierForm>(k: K, v: SupplierForm[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!form.name.trim()) return 'שם הספק הוא שדה חובה';
    const em = form.email.trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'כתובת אימייל אינה תקינה';
    return null;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!companyId) { setFormError('לא נבחרה חברה פעילה — אנא רענן את הדף'); return; }
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);

    const payload = {
      name:           form.name.trim(),
      contact_person: form.contact_person.trim() || null,
      tax_id:         form.tax_id.trim()         || null,
      email:          form.email.trim()           || null,
      phone:          form.phone.trim()           || null,
      payment_terms:  form.payment_terms.trim()   || null,
      currency:       form.currency,
      address:        form.address.trim()         || null,
      notes:          form.notes.trim()           || null,
    };

    try {
      if (editing) {
        const { error: e } = await supabase
          .from('inventory_suppliers')
          .update(payload)
          .eq('id', editing.id);
        if (e) throw e;
        showToast('ספק עודכן בהצלחה');
      } else {
        const { error: e } = await supabase
          .from('inventory_suppliers')
          .insert({ company_id: companyId, ...payload }); // companyId is non-null (guarded above)
        if (e) throw e;
        showToast('ספק נוסף בהצלחה');
      }
      closeModal();
      fetchSuppliers();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { error: e } = await supabase
      .from('inventory_suppliers')
      .delete()
      .eq('id', delTarget.id);
    if (e) { showToast(e.message, false); }
    else   { showToast('ספק נמחק'); setDelTarget(null); fetchSuppliers(); }
    setDeleting(false);
  };

  const filteredSuppliers = suppliers.filter(s => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    return (s.name?.toLowerCase().includes(q)) ||
           (s.contact_person?.toLowerCase().includes(q)) ||
           (s.phone?.includes(q)) ||
           (s.tax_id?.includes(q));
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Truck className="w-5 h-5 text-orange-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">ניהול ספקים</h2>
              <p className="text-xs text-gray-500">כרטסות ספקים, תנאי תשלום ומטבע עסקה</p>
            </div>
          </div>
          <button
            onClick={openAdd}
            disabled={!companyId}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700
                       text-white text-sm font-medium rounded-lg transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            הוסף ספק
          </button>
        </div>

        {!loading && suppliers.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="חיפוש לפי שם, איש קשר, טלפון או ח.פ..."
                className="w-full pr-10 pl-4 py-2 text-sm border border-gray-200 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400
                           placeholder:text-gray-400 transition-colors"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50
                          border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-14 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען ספקים…</span>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Truck className="w-10 h-10 text-gray-200" />
            <p className="text-sm font-medium">אין ספקים עדיין</p>
            <p className="text-xs text-gray-400">לחץ על "הוסף ספק" כדי להוסיף את הספק הראשון</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                               text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-right">מס׳</th>
                  <th className="px-4 py-3 text-right">שם הספק</th>
                  <th className="px-4 py-3 text-right">איש קשר</th>
                  <th className="px-4 py-3 text-right">טלפון</th>
                  <th className="px-4 py-3 text-right">אימייל</th>
                  <th className="px-4 py-3 text-right">תנאי תשלום</th>
                  <th className="px-4 py-3 text-right">מטבע</th>
                  <th className="px-4 py-3 text-right w-24">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredSuppliers.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">

                    {/* Supplier number badge */}
                    <td className="px-4 py-4 shrink-0">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1
                                       bg-orange-50 text-orange-700 text-xs font-bold
                                       rounded-lg border border-orange-200 tabular-nums">
                        <Hash className="w-3 h-3" />
                        {s.supplier_number}
                      </span>
                    </td>

                    {/* Name */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center
                                        justify-center text-orange-700 text-xs font-bold shrink-0">
                          {s.name[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 leading-tight">{s.name}</p>
                          {s.tax_id && (
                            <p className="text-xs text-gray-400 flex items-center gap-0.5">
                              <BadgeCheck className="w-3 h-3 shrink-0" />
                              {s.tax_id}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Contact person */}
                    <td className="px-4 py-4 text-gray-600 text-xs">
                      {s.contact_person ? (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3 text-gray-400 shrink-0" />
                          {s.contact_person}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Phone */}
                    <td className="px-4 py-4 text-gray-500 text-xs" dir="ltr">
                      {s.phone ? (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400 shrink-0" />
                          {s.phone}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Email */}
                    <td className="px-4 py-4 text-gray-500 text-xs" dir="ltr">
                      {s.email ? (
                        <a href={`mailto:${s.email}`}
                           className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                          <Mail className="w-3 h-3 text-gray-400 shrink-0" />{s.email}
                        </a>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Payment terms */}
                    <td className="px-4 py-4">
                      {s.payment_terms ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5
                                         bg-violet-50 text-violet-700 text-xs font-medium
                                         rounded-full border border-violet-200">
                          <Clock className="w-3 h-3" />
                          {s.payment_terms}
                        </span>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>

                    {/* Currency */}
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs
                                        font-semibold rounded-full border
                                        ${s.currency === 'ILS'
                                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                                          : s.currency === 'USD'
                                            ? 'bg-green-50 text-green-700 border-green-200'
                                            : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        <DollarSign className="w-3 h-3" />
                        {currencySymbol(s.currency)} {s.currency}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(s)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                     rounded-lg transition-colors"
                          title="ערוך"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDelTarget(s)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                     rounded-lg transition-colors"
                          title="מחק"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {suppliers.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">
            {suppliers.length} ספקים
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          Add / Edit Modal  (two-tab: כללי | פיננסי)
          ══════════════════════════════════════════════════════════════════════ */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4
                          flex flex-col max-h-[90vh]">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4
                            border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-gray-900">
                  {editing ? 'עריכת ספק' : 'הוספת ספק חדש'}
                </h3>
                {/* Show auto-generated number in edit mode */}
                {editing && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1
                                   bg-orange-50 text-orange-600 text-xs font-bold
                                   rounded-lg border border-orange-200">
                    <Hash className="w-3 h-3" />
                    {editing.supplier_number}
                  </span>
                )}
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 px-6 pt-4 shrink-0">
              {(['general', 'financial'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setModalTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${modalTab === tab
                      ? 'bg-orange-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                >
                  {tab === 'general' ? 'פרטים כלליים' : 'פיננסי ותנאים'}
                </button>
              ))}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

              {formError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50
                                border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{formError}
                </div>
              )}

              {/* ── TAB: פרטים כלליים ─────────────────────────────────────── */}
              {modalTab === 'general' && (
                <>
                  {/* Name */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      שם הספק <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setField('name', e.target.value)}
                      placeholder="לדוגמה: ייבוא כהן בע״מ"
                      autoFocus
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-400
                                 focus:border-transparent"
                    />
                  </div>

                  {/* Contact person */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      איש קשר
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <input
                      type="text"
                      value={form.contact_person}
                      onChange={e => setField('contact_person', e.target.value)}
                      placeholder="שם נציג הספק"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-400
                                 focus:border-transparent"
                    />
                  </div>

                  {/* Phone + Email — side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        טלפון
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={e => setField('phone', e.target.value)}
                        placeholder="03-0000000"
                        dir="ltr"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-orange-400
                                   focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        אימייל
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => setField('email', e.target.value)}
                        placeholder="supplier@example.com"
                        dir="ltr"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-orange-400
                                   focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      כתובת
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={e => setField('address', e.target.value)}
                      placeholder="רחוב, עיר, מיקוד"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-400
                                 focus:border-transparent"
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-gray-400" />
                      הערות
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <textarea
                      value={form.notes}
                      onChange={e => setField('notes', e.target.value)}
                      placeholder="מידע נוסף על הספק, אזורי אספקה…"
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400
                                 focus:border-transparent"
                    />
                  </div>
                </>
              )}

              {/* ── TAB: פיננסי ותנאים ──────────────────────────────────────── */}
              {modalTab === 'financial' && (
                <>
                  {/* Tax ID */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-violet-500" />
                      ח.פ / עוסק מורשה
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <input
                      type="text"
                      value={form.tax_id}
                      onChange={e => setField('tax_id', e.target.value)}
                      placeholder="000000000"
                      dir="ltr"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-violet-400
                                 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400">מספר עוסק מורשה / ח.פ של הספק</p>
                  </div>

                  {/* Currency */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5 text-green-500" />
                      מטבע עסקה
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {CURRENCIES.map(c => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setField('currency', c.value)}
                          className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2
                                      text-sm font-medium transition-all
                                      ${form.currency === c.value
                                        ? c.value === 'ILS'
                                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                                          : c.value === 'USD'
                                            ? 'border-green-500 bg-green-50 text-green-700'
                                            : 'border-amber-500 bg-amber-50 text-amber-700'
                                        : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}
                        >
                          <span className="text-xl font-bold">{c.symbol}</span>
                          <span className="text-xs">{c.value}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Payment terms */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-violet-500" />
                      תנאי תשלום
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    {/* Quick preset chips */}
                    <div className="flex flex-wrap gap-1.5 pb-1">
                      {PAYMENT_TERM_PRESETS.map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setField('payment_terms', p)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all
                                      ${form.payment_terms === p
                                        ? 'bg-violet-600 text-white border-violet-600'
                                        : 'bg-white text-gray-600 border-gray-300 hover:border-violet-400 hover:text-violet-700'}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                    {/* Free-text input */}
                    <input
                      type="text"
                      value={form.payment_terms}
                      onChange={e => setField('payment_terms', e.target.value)}
                      placeholder="או הזן תנאים מותאמים אישית…"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-violet-400
                                 focus:border-transparent"
                    />
                  </div>

                  {/* Supplier number — read-only info card in edit mode */}
                  {editing && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50
                                    border border-gray-200 rounded-xl">
                      <Hash className="w-4 h-4 text-gray-400 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-gray-600">מספר ספק (אוטומטי)</p>
                        <p className="text-lg font-bold text-orange-600 tabular-nums">
                          {editing.supplier_number}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400 mr-auto">
                        מספר זה נוצר אוטומטית ואינו ניתן לשינוי
                      </p>
                    </div>
                  )}
                </>
              )}

            </div>{/* end scrollable body */}

            {/* Modal footer */}
            <div className="flex items-center gap-3 justify-end px-6 py-4
                            border-t border-gray-100 shrink-0">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'שמור שינויים' : 'הוסף ספק'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Delete Confirm Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת ספק</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את הספק{' '}
                  <span className="font-semibold text-gray-800">
                    "{delTarget.name}"
                  </span>{' '}
                  <span className="text-orange-600 font-mono text-xs">
                    #{delTarget.supplier_number}
                  </span>?
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDelTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium
                           rounded-lg transition-colors"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
